import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  TrendingUp,
  DollarSign,
  Users,
  Target,
  Star,
  Wallet,
  Megaphone,
  RefreshCw,
  Save,
  Phone,
  PhoneCall,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Copy,
  Check,
  X,
} from 'lucide-react';
import { formatCurrency, PulseCard, formatWeekLabel } from './pulse/helpers';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PulseTab() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [saved, setSaved] = useState({ current: {}, previous: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cashPosition, setCashPosition] = useState('');
  const [adSpend, setAdSpend] = useState('');
  const [showZacMessage, setShowZacMessage] = useState(false);
  const [copied, setCopied] = useState(false);
  const [metaSpend, setMetaSpend] = useState({ configured: false, current: null, previous: null, error: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [metricsRes, reviewsRes, savedRes, metaCurrRes, metaPrevRes] = await Promise.all([
        fetch(`${API}/api/pulse/weekly-metrics?week_offset=${weekOffset}`),
        fetch(`${API}/api/pulse/reviews`),
        fetch(`${API}/api/pulse/saved?week_offset=${weekOffset}`),
        fetch(`${API}/api/pulse/meta-spend?week_offset=${weekOffset}`),
        fetch(`${API}/api/pulse/meta-spend?week_offset=${weekOffset - 1}`),
      ]);

      if (metricsRes.ok) setMetrics(await metricsRes.json());
      if (reviewsRes.ok) setReviews(await reviewsRes.json());

      let metaCurr = { configured: false, spend: null, error: null };
      let metaPrev = { configured: false, spend: null, error: null };
      if (metaCurrRes.ok) metaCurr = await metaCurrRes.json();
      if (metaPrevRes.ok) metaPrev = await metaPrevRes.json();
      setMetaSpend({
        configured: metaCurr.configured,
        current: metaCurr.spend,
        previous: metaPrev.spend,
        error: metaCurr.error,
      });

      if (savedRes.ok) {
        const s = await savedRes.json();
        setSaved(s);
        setCashPosition(s.current?.cashPosition ? String(s.current.cashPosition) : '');
        // Meta-synced spend takes precedence over the manually saved value
        if (metaCurr.configured && metaCurr.spend != null) {
          setAdSpend(String(metaCurr.spend));
        } else {
          setAdSpend(s.current?.adSpend ? String(s.current.adSpend) : '');
        }
      }
    } catch (e) {
      console.error('Failed to fetch pulse data:', e); // eslint-disable-line no-console
    }
    setLoading(false);
  }, [weekOffset]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/pulse/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cashPosition: cashPosition ? Number(cashPosition) : null,
          adSpend: adSpend ? Number(adSpend) : null,
          weekOffset,
        }),
      });
      await fetchData();
    } catch (e) {
      console.error('Failed to save:', e); // eslint-disable-line no-console
    }
    setSaving(false);
  };

  // Calculated metrics
  const hitchedMonthly = 317.12;
  const agencyMonthly = 1000;
  const weeklyFixedMarketing = (hitchedMonthly + agencyMonthly) / 4;

  const metaSynced = metaSpend.configured && metaSpend.current != null;
  const currAdSpend = metaSynced ? metaSpend.current : (saved.current?.adSpend || 0);
  const prevAdSpend = (metaSpend.configured && metaSpend.previous != null) ? metaSpend.previous : (saved.previous?.adSpend || 0);
  const totalMarketingCurr = currAdSpend + weeklyFixedMarketing;
  const totalMarketingPrev = prevAdSpend + weeklyFixedMarketing;

  const currBookings = metrics?.bookings?.current || 0;
  const prevBookings = metrics?.bookings?.previous || 0;
  const currLeads = metrics?.leads?.current || 0;
  const prevLeads = metrics?.leads?.previous || 0;
  const currCalls = metrics?.calls?.current || 0;
  const prevCalls = metrics?.calls?.previous || 0;
  const currRevenue = metrics?.revenue?.current || 0;
  const prevRevenue = metrics?.revenue?.previous || 0;

  const currCAC = currBookings > 0 ? totalMarketingCurr / currBookings : 0;
  const prevCAC = prevBookings > 0 ? totalMarketingPrev / prevBookings : 0;
  const cacChange = prevCAC > 0 ? Math.round(((currCAC - prevCAC) / prevCAC) * 1000) / 10 : (currCAC > 0 ? 100 : 0);

  const currCPL = currLeads > 0 ? totalMarketingCurr / currLeads : 0;
  const prevCPL = prevLeads > 0 ? totalMarketingPrev / prevLeads : 0;
  const cplChange = prevCPL > 0 ? Math.round(((currCPL - prevCPL) / prevCPL) * 1000) / 10 : (currCPL > 0 ? 100 : 0);

  const currCPC = currCalls > 0 ? totalMarketingCurr / currCalls : 0;
  const prevCPC = prevCalls > 0 ? totalMarketingPrev / prevCalls : 0;
  const cpcChange = prevCPC > 0 ? Math.round(((currCPC - prevCPC) / prevCPC) * 1000) / 10 : (currCPC > 0 ? 100 : 0);

  const currCash = saved.current?.cashPosition || 0;
  const prevCash = saved.previous?.cashPosition || 0;
  const cashChange = prevCash > 0 ? Math.round(((currCash - prevCash) / prevCash) * 1000) / 10 : (currCash > 0 ? 100 : 0);

  const weekLabel = formatWeekLabel(weekOffset);

  // Build Zac message
  const buildZacMessage = () => {
    const arrow = (change) => change > 0 ? `+${change}%` : change < 0 ? `${change}%` : 'no change';
    const adSpendChange = prevAdSpend > 0 ? Math.round(((currAdSpend - prevAdSpend) / prevAdSpend) * 1000) / 10 : (currAdSpend > 0 ? 100 : 0);
    const weekDates = formatWeekLabel(weekOffset);
    const lines = [
      `Weekly Pulse — ${weekDates}`,
      '',
      `Revenue: ${formatCurrency(currRevenue)} (${arrow(metrics?.revenue?.change || 0)} vs prev week)`,
      `Bookings: ${currBookings} (${arrow(metrics?.bookings?.change || 0)} vs prev week)`,
      `Leads: ${currLeads} (${arrow(metrics?.leads?.change || 0)} vs prev week)`,
      `Calls Booked: ${currCalls} (${arrow(metrics?.calls?.change || 0)} vs prev week)`,
      '',
      `CAC: ${formatCurrency(Math.round(currCAC))} (${arrow(cacChange)} vs prev week)`,
      `CPL: ${formatCurrency(Math.round(currCPL))} (${arrow(cplChange)} vs prev week)`,
      `Cost Per Call: ${formatCurrency(Math.round(currCPC))} (${arrow(cpcChange)} vs prev week)`,
      '',
      `FB Ad Spend: ${formatCurrency(currAdSpend)} (${arrow(adSpendChange)} vs prev week)`,
      `Cash Position: ${formatCurrency(currCash)} (${arrow(cashChange)} vs prev week)`,
      `Hitched Reviews: ${reviews?.hitched?.count || 0} (${reviews?.hitched?.rating || 0}/5)`,
    ];
    return lines.join('\n');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildZacMessage());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = buildZacMessage();
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6" data-testid="pulse-tab">
      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-stone-200"
            onClick={() => setWeekOffset(prev => prev - 1)}
            data-testid="week-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-center min-w-[200px]">
            <h2 className="text-xl font-semibold text-stone-800">
              {weekOffset === 0 ? 'This Week' : weekOffset === -1 ? 'Last Week' : 'Weekly Pulse'}
            </h2>
            <p className="text-sm text-stone-500">{weekLabel}</p>
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-stone-200"
            onClick={() => setWeekOffset(prev => Math.min(prev + 1, 0))}
            disabled={weekOffset >= 0}
            data-testid="week-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          {weekOffset !== 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-stone-500 hover:text-stone-700"
              onClick={() => setWeekOffset(0)}
              data-testid="week-today"
            >
              Today
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowZacMessage(true)}
            variant="outline"
            className="border-stone-200"
            data-testid="compose-zac-btn"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Compose Zac Message
          </Button>
          <Button
            onClick={fetchData}
            disabled={loading}
            variant="outline"
            className="border-stone-200"
            data-testid="refresh-pulse"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <PulseCard
          title="Revenue"
          current={currRevenue}
          previous={prevRevenue}
          change={metrics?.revenue?.change || 0}
          format="currency"
          icon={DollarSign}
          color="bg-green-50 text-green-600"
          subtitle="Won deals this week"
        />
        <PulseCard
          title="Bookings"
          current={currBookings}
          previous={prevBookings}
          change={metrics?.bookings?.change || 0}
          icon={Target}
          color="bg-blue-50 text-blue-600"
          subtitle="Won deals closed"
        />
        <PulseCard
          title="Leads"
          current={currLeads}
          previous={prevLeads}
          change={metrics?.leads?.change || 0}
          icon={Users}
          color="bg-purple-50 text-purple-600"
          subtitle="All new deals (any stage)"
        />
        <PulseCard
          title="Cash Position"
          current={currCash}
          previous={prevCash}
          change={currCash > 0 ? cashChange : null}
          format="currency"
          icon={Wallet}
          color="bg-amber-50 text-amber-600"
          subtitle="Manual input"
        />
      </div>

      {/* Calls Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <PulseCard
          title="Calls Booked"
          current={currCalls}
          previous={prevCalls}
          change={metrics?.calls?.change || 0}
          icon={Phone}
          color="bg-teal-50 text-teal-600"
          subtitle="Calls completed"
        />
        <PulseCard
          title="Cost Per Call"
          current={Math.round(currCPC)}
          previous={Math.round(prevCPC)}
          change={cpcChange}
          format="currency"
          icon={PhoneCall}
          color="bg-cyan-50 text-cyan-600"
          subtitle={`${formatCurrency(Math.round(totalMarketingCurr))} spend / ${currCalls} calls`}
        />
        <PulseCard
          title="CAC"
          current={Math.round(currCAC)}
          previous={Math.round(prevCAC)}
          change={cacChange}
          format="currency"
          icon={Megaphone}
          color="bg-orange-50 text-orange-600"
          subtitle={`${formatCurrency(Math.round(totalMarketingCurr))} spend / ${currBookings} bookings`}
        />
        <PulseCard
          title="CPL"
          current={Math.round(currCPL)}
          previous={Math.round(prevCPL)}
          change={cplChange}
          format="currency"
          icon={TrendingUp}
          color="bg-rose-50 text-rose-600"
          subtitle={`${formatCurrency(Math.round(totalMarketingCurr))} spend / ${currLeads} leads`}
        />
      </div>

      {/* Ad Spend + Reviews */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-stone-100 shadow-sm" data-testid="pulse-card-ad-spend">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-red-50 text-red-600">
                <Megaphone className="w-5 h-5" />
              </div>
              {metaSynced && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                  <Check className="w-3 h-3" /> Meta
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-stone-900 mb-1">{formatCurrency(currAdSpend)}</p>
            <p className="text-xs text-stone-500">Facebook Ad Spend</p>
            <p className="text-[11px] text-stone-400 mt-1">+ {formatCurrency(Math.round(weeklyFixedMarketing))}/wk fixed (Hitched + Agency)</p>
          </CardContent>
        </Card>

        {/* Hitched Reviews */}
        <Card className="bg-white border-stone-100 shadow-sm overflow-hidden" data-testid="pulse-card-hitched">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-pink-50 text-pink-600">
                <Star className="w-5 h-5" />
              </div>
              <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Live</span>
            </div>
            <p className="text-2xl font-bold text-stone-900 mb-1">{reviews?.hitched?.count || 0}</p>
            <p className="text-xs text-stone-500">Hitched Reviews</p>
            {reviews?.hitched?.rating > 0 && (
              <div className="flex items-center gap-1 mt-1.5">
                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                <span className="text-sm font-medium text-stone-700">{reviews.hitched.rating} out of 5</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Manual Input Section */}
      <Card className="bg-white border-stone-100 shadow-sm" data-testid="pulse-manual-input">
        <CardHeader className="pb-3 border-b border-stone-100">
          <CardTitle className="text-base font-medium text-stone-800">Weekly Input</CardTitle>
          <p className="text-xs text-stone-500 mt-1">
            Enter figures for {weekOffset === 0 ? 'this' : 'the selected'} week — saved per calendar week
          </p>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1.5 block">Cash Position (£)</label>
              <Input
                type="number"
                value={cashPosition}
                onChange={e => setCashPosition(e.target.value)}
                placeholder="e.g. 25000"
                data-testid="input-cash-position"
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-xs font-medium text-stone-600 block">FB Ad Spend (£)</label>
                {metaSynced ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5" data-testid="meta-synced-badge">
                    <Check className="w-3 h-3" /> Synced from Meta
                  </span>
                ) : metaSpend.configured && metaSpend.error ? (
                  <span className="inline-flex items-center text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="meta-error-badge" title={metaSpend.error}>
                    Meta not connected
                  </span>
                ) : null}
              </div>
              <Input
                type="number"
                value={adSpend}
                onChange={e => setAdSpend(e.target.value)}
                placeholder="e.g. 500"
                readOnly={metaSynced}
                className={metaSynced ? 'bg-stone-50 text-stone-500 cursor-not-allowed' : ''}
                data-testid="input-ad-spend"
              />
              {metaSynced && (
                <p className="text-[10px] text-stone-400 mt-1">Auto-pulled from Meta Ads for this week</p>
              )}
              {metaSpend.configured && metaSpend.error && (
                <p className="text-[10px] text-amber-600 mt-1" data-testid="meta-error-text">{metaSpend.error}</p>
              )}
            </div>
          </div>
          <div className="mt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-stone-900 text-white hover:bg-stone-800"
              data-testid="save-pulse-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save This Week'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Zac Message Modal */}
      {showZacMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="zac-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <h3 className="text-base font-semibold text-stone-800">Weekly Report for Zac</h3>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => { setShowZacMessage(false); setCopied(false); }}
                data-testid="zac-modal-close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-5">
              <pre className="whitespace-pre-wrap text-sm text-stone-700 bg-stone-50 rounded-lg p-4 font-mono leading-relaxed border border-stone-100"
                   data-testid="zac-message-content">
                {buildZacMessage()}
              </pre>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-stone-100 bg-stone-50/50">
              <Button
                variant="outline"
                className="border-stone-200"
                onClick={() => { setShowZacMessage(false); setCopied(false); }}
              >
                Close
              </Button>
              <Button
                onClick={handleCopy}
                className="bg-stone-900 text-white hover:bg-stone-800"
                data-testid="zac-copy-btn"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
